import { useState } from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  MonthEstimateAnswer,
  MonthlyExpenseEstimateFigures,
  MonthlyExpenseEstimateSide,
} from "../../types/monthly-expense.type";
import { formatNumber } from "../../utils";
import { formatMonthLabel, formatTimestamp } from "../DailyExpense/format";
import { TABLE_SX } from "../DailyExpense/tableStyles";
import { ESTIMATE_BADGE_TEXT, EMPTY_FIGURE_TEXT } from "./constants";

interface EstimateDetailProps {
  data: MonthEstimateAnswer;
  estimate: MonthlyExpenseEstimateFigures;
}

interface SideCardProps {
  title: string;
  perRouteDay: number | null;
  side: MonthlyExpenseEstimateSide;
  isOpen: boolean;
}

const figureOf = (value: number | null) => formatNumber(value, { fallback: EMPTY_FIGURE_TEXT });

/** Estimated figures are italic with the "ประมาณการ" badge, on every screen that shows them. */
function SideCard({ title, perRouteDay, side, isOpen }: Readonly<SideCardProps>) {
  return (
    <Paper sx={{ flex: 1, minWidth: 320 }}>
      <Stack gap={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography fontWeight={600}>{title}</Typography>
          <Chip size="small" label={ESTIMATE_BADGE_TEXT} />
        </Stack>
        <Typography variant="h4" fontWeight={600} sx={{ fontStyle: "italic", fontVariantNumeric: "tabular-nums" }}>
          {figureOf(perRouteDay)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          บาท ต่อสายต่อวัน · ยอดรวม {formatNumber(side.amount)} บาท
        </Typography>
        {isOpen && (
          <Table size="small" sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell>รายการ</TableCell>
                <TableCell align="right">ยอดเดือน</TableCell>
                <TableCell align="right">ต่อสายต่อวัน</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {side.lines.map((line, index) => (
                <TableRow key={`${line.source}-${line.name}-${index}`}>
                  <TableCell>{line.name}</TableCell>
                  <TableCell align="right">{formatNumber(line.amount)}</TableCell>
                  <TableCell align="right" sx={{ fontStyle: "italic" }}>
                    {figureOf(line.per_route_day)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
    </Paper>
  );
}

/** S5 body: the month, what it is computed from, Sales and Manager per route per day, and the lines. */
export default function EstimateDetail({ data, estimate }: Readonly<EstimateDetailProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = () => setIsOpen((previous) => !previous);

  return (
    <Stack gap={2}>
      <Paper>
        <Stack gap={0.5}>
          <Typography fontWeight={600}>
            สาขา {data.branch_code} {data.branch_name} · ประมาณการ {formatMonthLabel(data.month)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            คิดจากเดือน {formatMonthLabel(data.source_month)} · วันขายจริง {estimate.sale_days} วัน · จำนวนสายจริง{" "}
            {estimate.route_count} สาย
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ส่งเมื่อ {formatTimestamp(estimate.sent_at)} · {estimate.sent_by_name ?? EMPTY_FIGURE_TEXT}
          </Typography>
        </Stack>
      </Paper>
      <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
        <SideCard title="Sales (ฝ่ายขาย)" perRouteDay={estimate.sales_per_route_day} side={estimate.sales} isOpen={isOpen} />
        <SideCard title="Manager (ฝ่ายบริหาร)" perRouteDay={estimate.manager_per_route_day} side={estimate.manager} isOpen={isOpen} />
      </Stack>
      <Stack direction="row">
        <Button variant="outlined" onClick={toggleOpen}>
          {isOpen ? "ซ่อนรายละเอียดรายการ" : "ดูรายละเอียดรายการ"}
        </Button>
      </Stack>
    </Stack>
  );
}
